/* =========================================================================
 * Maqam — Saudi Arabia's AI-powered job-application & clinical-training platform.
 * Copyright (c) 2026 Maqam. All rights reserved. Riyadh, Kingdom of Saudi Arabia.
 *
 * This source code is proprietary and confidential. Protected under Saudi
 * Royal Decree M/41 (2003), the Berne Convention, and TRIPS. No part of
 * this software may be reproduced, distributed, modified, sub-licensed,
 * or used to train AI models without prior written consent.
 *
 * License: see /LICENSE  ·  Contact: legal@maqam-ksa.com
 * ========================================================================= */
import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { I18nProvider } from "@/context/I18nContext";
import { Toaster } from "sonner";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import OAuthFinish from "@/pages/OAuthFinish";
import AppLayout from "@/components/layout/AppLayout";
import Dashboard from "@/pages/Dashboard";
import Search from "@/pages/Search";
import Applications from "@/pages/Applications";
import Inbox from "@/pages/Inbox";
import Profile from "@/pages/Profile";
import Settings from "@/pages/Settings";
import CVReview from "@/pages/CVReview";
import CVBuilder from "@/pages/CVBuilder";
import Skills from "@/pages/Skills";
import News from "@/pages/News";
import Tips from "@/pages/Tips";
import BecomeOneOfUs from "@/pages/BecomeOneOfUs";
import RecruiterRegister from "@/pages/RecruiterRegister";
import RecruiterDashboard from "@/pages/RecruiterDashboard";
import Hire from "@/pages/Hire";
import Agencies from "@/pages/Agencies";
import AgencyRegister from "@/pages/AgencyRegister";
import AgencyDashboard from "@/pages/AgencyDashboard";
import BecomePremium from "@/pages/BecomePremium";
import IqTest from "@/pages/IqTest";
import RecruiterPremiumMembers from "@/pages/RecruiterPremiumMembers";
import Interview from "@/pages/Interview";
import SalaryIntel from "@/pages/SalaryIntel";
import Alerts from "@/pages/Alerts";
import Billing from "@/pages/Billing";
import DealCloser from "@/pages/DealCloser";
import HRDF from "@/pages/HRDF";
import CareerGPS from "@/pages/CareerGPS";
import Subscriptions from "@/pages/Subscriptions";
import Certificates from "@/pages/Certificates";
import Investors from "@/pages/Investors";
import InvestorJoin from "@/pages/InvestorJoin";
import InvestorFeed from "@/pages/InvestorFeed";
import Founders from "@/pages/Founders";
import Privacy from "@/pages/Privacy";
import Admin from "@/pages/Admin";
import WeeklyBrief from "@/pages/WeeklyBrief";
import MockInterview from "@/pages/MockInterview";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import Terms from "@/pages/Terms";
import Copyright from "@/pages/Copyright";
import StudyAbroad from "@/pages/StudyAbroad";

/**
 * Global guard: any unhandled axios rejection (page forgot a `.catch()`)
 * gets swallowed here so the dev "Uncaught runtime errors" overlay never
 * blocks the UI on a transient backend hiccup. We surface a discreet toast
 * instead. Real auth (401) and network errors still flow through normally.
 */
if (typeof window !== "undefined" && !window.__maqamRejectionGuard) {
  window.__maqamRejectionGuard = true;
  window.addEventListener("unhandledrejection", (event) => {
    const err = event.reason;
    // Only intercept axios-shaped errors so other unhandled rejections still surface in dev.
    if (err && (err.isAxiosError || err.config || err.response)) {
      event.preventDefault();
      const status = err.response?.status;
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.warn("[axios-unhandled]", status || err.code || err.message, err.config?.url);
      }
      // Don't double-toast on auth (handled elsewhere) or aborted requests.
      if (status === 401 || err.code === "ERR_CANCELED") return;
    }
  });
}

const Protected = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="h-screen flex items-center justify-center font-mono text-sm">loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
};

function App() {
  return (
    <div className="App">
      <I18nProvider>
        <AuthProvider>
          <BrowserRouter>
            <Toaster position="top-center" />
            <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/privacy" element={<Navigate to="/privacy-policy" replace />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/copyright" element={<Copyright />} />
              <Route path="/subscriptions" element={<Subscriptions />} />
              <Route path="/for-investors" element={<Investors />} />
              <Route path="/investors" element={<InvestorJoin />} />
              <Route path="/founders" element={<Founders />} />
              <Route path="/news" element={<News />} />
              <Route path="/study-abroad" element={<StudyAbroad />} />
              <Route path="/hire" element={<Hire />} />
              <Route path="/for-employers" element={<Hire />} />
              <Route path="/agencies" element={<Agencies />} />
              <Route path="/agencies/register" element={<AgencyRegister />} />
              <Route path="/agencies/dashboard" element={<AgencyDashboard />} />
              <Route path="/become-premium" element={<BecomePremium />} />
              <Route path="/recruiter/premium-members" element={<Navigate to="/app/premium-members" replace />} />
              <Route path="/become-premium/iq-test" element={<IqTest />} />
              <Route path="/tips" element={<Tips />} />
              <Route path="/become-one-of-us" element={<BecomeOneOfUs />} />
              <Route path="/recruiter/register" element={<RecruiterRegister />} />
              <Route
                path="/recruiter/search"
                element={
                  <Protected>
                    <RecruiterDashboard />
                  </Protected>
                }
              />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/oauth/finish" element={<OAuthFinish />} />
              <Route
                path="/app"
                element={
                  <Protected>
                    <AppLayout />
                  </Protected>
                }
              >
                <Route index element={<Navigate to="dashboard" replace />} />
                <Route path="dashboard" element={<Dashboard />} />
                <Route path="search" element={<Search />} />
                <Route path="applications" element={<Applications />} />
                <Route path="inbox" element={<Inbox />} />
                <Route path="profile" element={<Profile />} />
                <Route path="settings" element={<Settings />} />
                <Route path="cv-review" element={<CVReview />} />
                <Route path="cv-builder" element={<CVBuilder />} />
                <Route path="skills" element={<Skills />} />
                <Route path="mock-interview" element={<MockInterview />} />
                <Route path="salary" element={<SalaryIntel />} />
                <Route path="alerts" element={<Alerts />} />
                <Route path="billing" element={<Billing />} />
                <Route path="deal-closer" element={<DealCloser />} />
                <Route path="certificates" element={<Certificates />} />
                <Route path="weekly-brief" element={<WeeklyBrief />} />
                <Route path="privacy" element={<Privacy />} />
                <Route path="admin" element={<Admin />} />
                <Route path="premium-members" element={<RecruiterPremiumMembers />} />
                <Route path="investor-feed" element={<InvestorFeed />} />
                {/* Redirects for removed nav items */}
                <Route path="interview" element={<Navigate to="/app/mock-interview" replace />} />
                <Route path="career-gps" element={<Navigate to="/app/dashboard" replace />} />
                <Route path="hrdf" element={<Navigate to="/app/dashboard" replace />} />
              </Route>
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
        </AuthProvider>
      </I18nProvider>
    </div>
  );
}

export default App;
